import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getSession } from './useSession.ts';
import { toast } from 'sonner';
import { useEffect } from 'react';


// Helper function to make authenticated requests
async function makePostmarkRequest(
  endpoint: string, 
  method: 'GET' | 'POST' | 'DELETE' = 'GET', 
  body?: any
) {
  const session = await getSession();

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/add-domain-email${endpoint}`,
    {
      method,
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      ...(body && { body: JSON.stringify(body) })
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: Request failed`);
  }

  return response.json();
}

// Hook to add domain to Postmark
export function useAddDomainToPostmark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ domain, emailPrefix }: { domain: string; emailPrefix?: string }) => 
      makePostmarkRequest('/add', 'POST', { domain, emailPrefix }),
    
    onSuccess: (data, variables) => {
      if (data.success) {
        toast.success(`Domain ${variables.domain} added to Postmark successfully!`);
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['user-domain'] });
        queryClient.invalidateQueries({ queryKey: ['postmark-status'] });
      } else {
        toast.error(data.error || 'Failed to add domain to Postmark');
      }
    },
    
    onError: (error) => {
      console.error('Error adding domain to Postmark:', error);
      toast.error(error.message || 'Failed to add domain to Postmark');
    }
  });
}


// Hook to check domain email status and update database
export function useCheckEmailStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (senderId: number) => makePostmarkRequest(`/status?emailId=${encodeURIComponent(senderId)}`),
    
    onSuccess: () => {
      toast.success(`Domain status updated successfully`);
      queryClient.invalidateQueries({ queryKey: ['user-domain'] });
      queryClient.invalidateQueries({ queryKey: ['postmark-status'] });
    },
    onError: (error) => {
      console.error('Error checking domain status:', error);
      toast.error(error.message || 'Failed to check domain status');
    }
  });
}

// Hook to remove domain from Postmark
export function useRemoveDomainFromPostmark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => makePostmarkRequest('/remove', 'DELETE'),
    
    onSuccess: (data) => {
      if (data.success) {
        toast.success(`Domain removed from Postmark`);
        
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['user-domain'] });
        queryClient.invalidateQueries({ queryKey: ['postmark-status'] });
      }
    },
    
    onError: (error) => {
      console.error('Error removing domain from Postmark:', error);
      toast.error(error.message || 'Failed to remove domain from Postmark');
    }
  });
}

// Hook for automatic email status checking with setInterval
export function useAutoEmailStatusCheck(senderId: number | undefined, emailStatus: string | undefined) {
  const { mutate: checkEmailStatus } = useCheckEmailStatus();

  useEffect(() => {
    if (!senderId || emailStatus !== 'pending') return;

    const interval = setInterval(() => {
      console.log('Auto-checking email status...');
      checkEmailStatus(senderId);
    }, 60000); // 1 minute

    return () => clearInterval(interval);
  }, [senderId, emailStatus, checkEmailStatus]);
}
